import { useState } from 'react';
import { hasCoPowers, displayNameForUser } from '@cosvision/shared';
import { useAuthStore } from '../../store/auth-store.js';
import { useAiDrawerStore } from '../../store/ai-drawer-store.js';
import { useSidebarStore } from '../../store/sidebar-store.js';
import { formatDateTime } from '../../lib/format.js';
import { InboxBell } from '../notifications/InboxBell.js';

export function Header() {
  const user = useAuthStore((s) => s.user);
  const logout = useAuthStore((s) => s.logout);
  const sidebarExpanded = useSidebarStore((s) => s.isExpanded);
  const toggleSidebar = useSidebarStore((s) => s.toggle);
  const toggleMobileNav = useSidebarStore((s) => s.toggleMobile);
  const [loggingOut, setLoggingOut] = useState(false);

  async function handleLogout(): Promise<void> {
    setLoggingOut(true);
    try {
      await logout();
    } finally {
      setLoggingOut(false);
    }
  }

  return (
    <header className="flex items-center justify-between gap-2 border-b border-slate-200 bg-white px-4 py-3 shadow-sm sm:gap-3 sm:px-6">
      <div className="flex min-w-0 items-center gap-2 sm:gap-3">
        {/* Mobile: hamburger toggles the off-canvas nav drawer. */}
        <button
          type="button"
          onClick={toggleMobileNav}
          className="inline-flex rounded-md p-2 text-slate-500 hover:bg-slate-100 hover:text-slate-700 focus:outline-none focus:ring-2 focus:ring-bn-300 md:hidden"
          aria-label="Open navigation menu"
        >
          <HamburgerIcon />
        </button>
        {/* Desktop: collapse/expand the persistent rail. */}
        <button
          type="button"
          onClick={toggleSidebar}
          className="hidden rounded-md p-1.5 text-slate-500 hover:bg-slate-100 hover:text-slate-700 focus:outline-none focus:ring-2 focus:ring-bn-300 md:inline-flex"
          aria-label={sidebarExpanded ? 'Collapse sidebar' : 'Expand sidebar'}
          aria-pressed={sidebarExpanded}
          title={sidebarExpanded ? 'Collapse sidebar' : 'Expand sidebar'}
        >
          <HamburgerIcon className="h-4 w-4" />
        </button>
        <div className="min-w-0 truncate text-sm text-slate-500">
          {/* Full date from sm up; compact date on phones. */}
          <span className="hidden sm:inline">
            {formatDateTime(new Date(), 'dddd, DD MMM YYYY')}
            <span className="ml-3 text-xs text-slate-400">(Asia/Dhaka)</span>
          </span>
          <span className="sm:hidden">{formatDateTime(new Date(), 'DD MMM')}</span>
        </div>
      </div>
      <div className="flex shrink-0 items-center gap-2 sm:gap-3">
        {hasCoPowers(user) ? <AiLauncherButton /> : null}
        <InboxBell
          enabled={user?.role === 'CO' || user?.role === 'OFFICER'}
          alertsEnabled={hasCoPowers(user)}
        />
        <div className="hidden text-right md:block">
          <div className="text-sm font-medium text-slate-900">
            {user ? displayNameForUser(user) : ''}
          </div>
          <div className="text-xs text-slate-500">
            {user?.role === 'CO' ? 'Commanding Officer' : (user?.rank ?? user?.role)}
          </div>
        </div>
        {/* Full label from sm up; icon-only sign-out on phones. */}
        <button
          type="button"
          className="btn-secondary hidden sm:inline-flex"
          onClick={() => {
            void handleLogout();
          }}
          disabled={loggingOut}
        >
          {loggingOut ? 'Signing out…' : 'Sign out'}
        </button>
        <button
          type="button"
          className="inline-flex rounded-md p-2 text-slate-500 hover:bg-slate-100 hover:text-slate-700 focus:outline-none focus:ring-2 focus:ring-bn-300 disabled:opacity-50 sm:hidden"
          onClick={() => {
            void handleLogout();
          }}
          disabled={loggingOut}
          aria-label="Sign out"
          title="Sign out"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.7"
            strokeLinecap="round"
            strokeLinejoin="round"
            className="h-5 w-5"
            aria-hidden="true"
          >
            <path d="M9 21H5a2 2 0 01-2-2V5a2 2 0 012-2h4M16 17l5-5-5-5M21 12H9" />
          </svg>
        </button>
      </div>
    </header>
  );
}

function HamburgerIcon({ className = 'h-5 w-5' }: { className?: string }) {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.7"
      strokeLinecap="round"
      strokeLinejoin="round"
      className={className}
      aria-hidden="true"
    >
      <path d="M3 6h18M3 12h18M3 18h18" />
    </svg>
  );
}

function AiLauncherButton() {
  const open = useAiDrawerStore((s) => s.open);
  return (
    <button
      type="button"
      onClick={open}
      aria-label="AI Co-Pilot"
      title="AI Co-Pilot"
      className="inline-flex items-center gap-1.5 rounded-full bg-bn-100 p-2 text-xs font-medium text-bn-800 hover:bg-bn-200 focus:outline-none focus:ring-2 focus:ring-bn-300 sm:px-3 sm:py-1.5"
    >
      <svg
        xmlns="http://www.w3.org/2000/svg"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.7"
        strokeLinecap="round"
        strokeLinejoin="round"
        className="h-3.5 w-3.5"
        aria-hidden="true"
      >
        <path d="M12 3v3M12 18v3M3 12h3M18 12h3M5.6 5.6l2.1 2.1M16.3 16.3l2.1 2.1M5.6 18.4l2.1-2.1M16.3 7.7l2.1-2.1" />
        <circle cx="12" cy="12" r="3" />
      </svg>
      <span className="hidden sm:inline">AI Co-Pilot</span>
    </button>
  );
}
